import { makeAutoObservable } from 'mobx'
import {
  ShippingModule,
  ShippingSecret,
  ShippingSettingNotificationsType,
  ShippingZoneCountry,
  ShippingZoneRegion,
  UpdatedTabsTypes,
} from './types'
import { generateUEID } from '~/shared/lib/helper-functions/ueid'

export const DEFAULT_NOTIFICATION_TEXT = {
  RU: '',
  EN: '',
}

export const DEFAULT_NOTIFICATIONS: ShippingSettingNotificationsType = {
  id: '',
  store_id: '',
  shipping_provider: '',
  notification_enabled: false,
  notification_region: 'ALL',
  notification_text: DEFAULT_NOTIFICATION_TEXT,
  created_at: '',
  updated_at: '',
  deleted_at: null,
}

const DEFAULT_STATE_TABS = {
  notifications: false,
  countries: false,
  regions: false,
  secrets: false,
  modules: false,
}

export class SettingShippingStore {
  shippingSettingsNotifications: ShippingSettingNotificationsType =
    DEFAULT_NOTIFICATIONS

  shippingZonesCountries: ShippingZoneCountry[] = []

  shippingZonesCountriesServer: ShippingZoneCountry[] = []

  shippingZonesRegions: ShippingZoneRegion[] = []

  shippingZonesRegionsServer: ShippingZoneRegion[] = []

  shippingProviderSecrets: ShippingSecret[] = []

  shippingProviderModules: ShippingModule[] = []

  shippingProviderSecretsOriginal: Record<string, string> = {}

  shippingProviderModulesOriginal: Record<string, string> = {}

  updatedTabs: UpdatedTabsTypes = DEFAULT_STATE_TABS

  idsToDeleteRegion: string[] = []

  idsToDeleteCountry: string[] = []

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setUpdatedTabs = (tabName: keyof UpdatedTabsTypes, flag: boolean) => {
    this.updatedTabs[tabName] = flag
  }

  setDefaultTabs = () => {
    this.updatedTabs = DEFAULT_STATE_TABS
  }

  setShippingSettingsNotifications = (
    data: ShippingSettingNotificationsType,
  ) => {
    this.shippingSettingsNotifications = {
      ...data,
      notification_text: data.notification_text || {
        ...DEFAULT_NOTIFICATION_TEXT,
      },
    }
  }

  setShippingZonesCountries = (data: ShippingZoneCountry[]) => {
    this.shippingZonesCountries = data
    this.shippingZonesCountriesServer = data
  }

  setShippingZonesRegions = (data: ShippingZoneRegion[]) => {
    this.shippingZonesRegions = data
    this.shippingZonesRegionsServer = data
  }

  setShippingProviderSecrets = (data: ShippingSecret[]) => {
    this.shippingProviderSecrets = data
    this.shippingProviderSecretsOriginal = Object.fromEntries(
      (data || []).map((secret) => [secret.key, secret.value || '']),
    )
  }

  setShippingProviderModules = (data: ShippingModule[]) => {
    this.shippingProviderModules = data
    this.shippingProviderModulesOriginal = Object.fromEntries(
      (data || []).map((module) => [module.key, module.value || '']),
    )
  }

  updateShippingZoneCountry = (
    countryId: string,
    updates: Partial<ShippingZoneCountry>,
  ) => {
    this.shippingZonesCountries = this.shippingZonesCountries.map((country) =>
      country.id === countryId ? { ...country, ...updates } : country,
    )
  }

  updateRate = (
    countryId: string,
    rateIndex: number,
    field: 'value' | 'price',
    newValue: string,
  ) => {
    this.shippingZonesCountries = this.shippingZonesCountries.map((country) => {
      if (country.id !== countryId) return country

      const updatedRates = [...(country.rates || [])]

      if (!updatedRates[rateIndex]) {
        updatedRates[rateIndex] = { value: '', price: '' }
      }

      updatedRates[rateIndex] = {
        ...updatedRates[rateIndex],
        [field]: newValue,
      }

      return {
        ...country,
        rates: updatedRates,
      }
    })
  }

  updateCountryName = (countryId: string, name: string) => {
    this.shippingZonesCountries = this.shippingZonesCountries.map((country) =>
      country.id === countryId ? { ...country, name } : country,
    )
  }

  updateRegionName = (regionId: string, name: string) => {
    this.shippingZonesRegions = this.shippingZonesRegions.map((region) =>
      region.id === regionId ? { ...region, name } : region,
    )
  }

  addRate = (countryId: string) => {
    this.shippingZonesCountries = this.shippingZonesCountries.map((country) =>
      country.id === countryId
        ? {
            ...country,
            rates: [...(country.rates || []), { value: '', price: '' }],
          }
        : country,
    )
  }

  addRegionRate = (regionId: string) => {
    this.shippingZonesRegions = this.shippingZonesRegions.map((region) =>
      region.id === regionId
        ? {
            ...region,
            rates: [...(region.rates || []), { value: '', price: '' }],
          }
        : region,
    )
  }

  removeRate = (countryId: string, rateIndex: number) => {
    this.shippingZonesCountries = this.shippingZonesCountries.map((country) =>
      country.id === countryId
        ? {
            ...country,
            rates:
              country.rates?.filter((_, i) => {
                return i !== rateIndex
              }) || [],
          }
        : country,
    )
  }

  removeRegionRate = (regionId: string, rateIndex: number) => {
    this.shippingZonesRegions = this.shippingZonesRegions.map((region) =>
      region.id === regionId
        ? {
            ...region,
            rates: region.rates?.filter((_, i) => i !== rateIndex) || [],
          }
        : region,
    )
  }

  addCountryCard = (storeId: string, provider: string, name: string) => {
    const newCountry: ShippingZoneCountry = {
      id: generateUEID(),
      name,
      countries: [],
      rates: [{ value: '', price: '' }],
      storeId,
      shippingProvider: provider,
      country: null,
      countryRegions: [{ code: '', name: '' }],
    }

    this.shippingZonesCountries = [...this.shippingZonesCountries, newCountry]
  }

  addRegionCard = (newRegion: ShippingZoneRegion) => {
    this.shippingZonesRegions = [...this.shippingZonesRegions, newRegion]
  }

  removeCountryCard = async (countryId: string) => {
    const shippingZonesCountrieServerCopy = [
      ...this.shippingZonesCountriesServer,
    ]

    const serverId = shippingZonesCountrieServerCopy.find(
      (el) => el.id === countryId,
    )?.id

    if (serverId) {
      this.idsToDeleteCountry = [...this.idsToDeleteCountry, serverId]
    }

    this.shippingZonesCountries = this.shippingZonesCountries.filter(
      (country) => country.id !== countryId,
    )
  }

  removeRegionCard = (regionId: string) => {
    const shippingZonesRegionsServerCopy = [...this.shippingZonesRegionsServer]

    const serverId = shippingZonesRegionsServerCopy.find(
      (el) => el.id === regionId,
    )?.id

    if (serverId) {
      this.idsToDeleteRegion = [...this.idsToDeleteRegion, serverId]
    }

    this.shippingZonesRegions = this.shippingZonesRegions.filter(
      (region) => region.id !== regionId,
    )
  }

  clearIdsDelete = () => {
    this.idsToDeleteCountry = []
    this.idsToDeleteRegion = []
  }

  updateShippingZoneRegion = (
    regionId: string,
    updates: Partial<ShippingZoneRegion>,
  ) => {
    this.shippingZonesRegions = this.shippingZonesRegions.map((region) =>
      region.id === regionId ? { ...region, ...updates } : region,
    )
  }

  updateRegionRate = (
    regionId: string,
    rateIndex: number,
    field: 'value' | 'price',
    newValue: string,
  ) => {
    this.shippingZonesRegions = this.shippingZonesRegions.map((region) => {
      if (region.id !== regionId) return region

      const updatedRates = [...(region.rates || [])]

      if (!updatedRates[rateIndex]) {
        updatedRates[rateIndex] = { value: '', price: '' }
      }

      updatedRates[rateIndex] = {
        ...updatedRates[rateIndex],
        [field]: newValue,
      }

      return {
        ...region,
        rates: updatedRates,
      }
    })
  }

  updateNotificationEnabled = (enabled: boolean) => {
    if (this.shippingSettingsNotifications) {
      this.shippingSettingsNotifications.notification_enabled = enabled
    }
  }

  updateNotificationRegion = (region: string) => {
    if (this.shippingSettingsNotifications) {
      this.shippingSettingsNotifications.notification_region = region
    }
  }

  updateNotificationText = (lang: string, text: string) => {
    if (this.shippingSettingsNotifications?.notification_text) {
      this.shippingSettingsNotifications.notification_text[lang] = text
    }
  }

  updateSecretValue = (secretId: string, value: string) => {
    const secret = this.shippingProviderSecrets.find((s) => s.key === secretId)
    if (secret) {
      secret.value = value
    }
  }

  updateSecretName = (secretId: string, name: string) => {
    const secret = this.shippingProviderSecrets.find((s) => s.key === secretId)
    if (secret) {
      secret.name = name
    }
  }

  updateModuleValue = (moduleId: string, value: string) => {
    const module = this.shippingProviderModules.find((m) => m.key === moduleId)
    if (module) {
      module.value = value
    }
  }
}
